<template>
  <v-container>
    <v-row v-if="showTitleBar">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card-title class="primary--text pa-0 pl-2 pb-2">
          <img
            src="/images/General Disease Severity-icon-128.png"
            title="General Disease Severity"
            style="height:1.5em"
            class="ma-1"
          />
          {{ title }}

          <v-spacer />

          <v-btn
            :x-small="exportButtonSize == 'x-small'"
            :small="exportButtonSize == 'small'"
            class="primary mx-2"
            @click="exportPlotDialog = true"
            ><v-icon left>mdi-download</v-icon>Export plot</v-btn
          >
        </v-card-title>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <!-- standard frequencies -->
        <audiogram-plot
          ref="standard_freqs"
          :analysis="analysis"
          :visible-cohorts="visibleCohorts"
          :show-left-air="showLeftAir"
          :show-right-air="showRightAir"
          :show-left-bone="showLeftBone"
          :show-right-bone="showRightBone"
          :show-affected-air="showAffectedAir"
          :show-unaffected-air="showUnaffectedAir"
          :show-affected-bone="showAffectedBone"
          :show-unaffected-bone="showUnaffectedBone"
          :show-contralateral-air="showContralateralAir"
          :show-ipsilateral-air="showIpsilateralAir"
          :show-contralateral-bone="showContralateralBone"
          :show-ipsilateral-bone="showIpsilateralBone"
          :show-linking-lines="showLinkingLines"
          :show-boxplots="showBoxplots"
          :show-error-bars="showErrorBars"
          :jitter-groups="jitterGroups"
          :show-median="showMedian"
          :show-connecting-lines="showConnectingLines"
          :show-grid="showGrid"
          :show-hearing-loss-zones="showHearingLossZones"
          :show-significant-freqs="showSignificantFreqs"
          :size="plotSize"
          :y-axis-title="
            analysis != null && analysis.input.comparisonField == 'change'
              ? 'Change in hearing level in dB'
              : 'Hearing level in dB'
          "
          :highlight-pval="pval_threshold"
          :min-db="haveHighFrequencyData && showHighFreqs ? -20 : -10"
        >
        </audiogram-plot>

        <!-- extended / high frequencies -->
        <audiogram-plot
          v-if="haveHighFrequencyData && showHighFreqs"
          ref="high_freqs"
          :analysis="analysis"
          :visible-cohorts="visibleCohorts"
          :show-high-freqs="true"
          :show-left-air="showLeftAir"
          :show-right-air="showRightAir"
          :show-left-bone="showLeftBone"
          :show-right-bone="showRightBone"
          :show-affected-air="showAffectedAir"
          :show-unaffected-air="showUnaffectedAir"
          :show-affected-bone="showAffectedBone"
          :show-unaffected-bone="showUnaffectedBone"
          :show-contralateral-air="showContralateralAir"
          :show-ipsilateral-air="showIpsilateralAir"
          :show-contralateral-bone="showContralateralBone"
          :show-ipsilateral-bone="showIpsilateralBone"
          :show-linking-lines="showLinkingLines"
          :show-boxplots="showBoxplots"
          :show-error-bars="showErrorBars"
          :jitter-groups="jitterGroups"
          :show-median="showMedian"
          :show-connecting-lines="showConnectingLines"
          :show-grid="showGrid"
          :show-hearing-loss-zones="false"
          :show-significant-freqs="showSignificantFreqs"
          :size="plotSize"
          :highlight-pval="pval_threshold"
          :min-db="haveHighFrequencyData && showHighFreqs ? -20 : -10"
        >
        </audiogram-plot>
      </v-col>
    </v-row>

    <!-- key/controls -->
    <v-row v-if="showControls">
      <v-col cols="1">
        <v-container fluid class="ma-0 pa-0" style="position: relative;">
          <v-row align="center" justify="end" class="pt-1">
            <span class="pr-3">Air:</span>
          </v-row>
        </v-container>
      </v-col>
      <v-col cols="11">
        <v-container
          fluid
          fill-width
          class="ma-0 pa-0"
          :style="'position:relative;'"
        >
          <v-row align="center" justify="start">
            <svg width="20" height="20" class="ma-2">
              <line
                x1="0"
                y1="0"
                x2="18"
                y2="18"
                fill="none"
                :stroke="showLeftAir ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
              <line
                x1="0"
                y1="18"
                x2="18"
                y2="0"
                fill="none"
                :stroke="showLeftAir ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
            </svg>
            <span :disabled="!showLeftAir">Left ear</span>
            <v-checkbox
              v-model="showLeftAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <svg width="20" height="20" class="ma-2 ml-5">
              <circle
                r="9"
                cx="10"
                cy="10"
                fill="none"
                :stroke="showRightAir ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></circle>
            </svg>
            <span :disabled="!showRightAir">Right ear</span>
            <v-checkbox
              v-model="showRightAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <!-- affected/unaffected -->
            <svg v-if="haveAffectedEarData" width="20" height="20" class="ma-2">
              <rect
                x="0"
                y="0"
                width="20"
                height="20"
                :fill="showAffectedAir ? '#3f51b5' : '#d0d0d0'"
                :stroke="showAffectedAir ? '#3f51b5' : '#d0d0d0'"
                stroke-width="2"
              ></rect>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.34em"
              >
                A
              </text>
            </svg>
            <span v-if="haveAffectedEarData" :disabled="!showAffectedAir"
              >Affected ear</span
            >
            <v-checkbox
              v-if="haveAffectedEarData"
              v-model="showAffectedAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>
            <svg
              v-if="haveAffectedEarData"
              width="20"
              height="20"
              class="ma-2 ml-5"
            >
              <rect
                x="0"
                y="0"
                width="20"
                height="20"
                :fill="showUnaffectedAir ? '#3f51b5' : '#d0d0d0'"
                :stroke="showUnaffectedAir ? '#3f51b5' : '#d0d0d0'"
                stroke-width="2"
              ></rect>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.34em"
              >
                U
              </text>
            </svg>

            <span v-if="haveAffectedEarData" :disabled="!showUnaffectedAir"
              >Unaffected ear</span
            >
            <v-checkbox
              v-if="haveAffectedEarData"
              v-model="showUnaffectedAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>
            <!-- affected/unaffected -->

            <!-- contralateral/ipsilateral -->
            <svg
              v-if="haveContraIpsiEarData"
              width="20"
              height="20"
              class="ma-2"
            >
              <rect
                x="0"
                y="0"
                width="20"
                height="20"
                :fill="showContralateralAir ? '#3f51b5' : '#d0d0d0'"
                :stroke="showContralateralAir ? '#3f51b5' : '#d0d0d0'"
                stroke-width="2"
              ></rect>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.34em"
              >
                C
              </text>
            </svg>
            <span v-if="haveContraIpsiEarData" :disabled="!showAffectedAir"
              >Contralateral ear</span
            >
            <v-checkbox
              v-if="haveContraIpsiEarData"
              v-model="showContralateralAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <svg
              v-if="haveContraIpsiEarData"
              width="20"
              height="20"
              class="ma-2 ml-5"
            >
              <rect
                x="0"
                y="0"
                width="20"
                height="20"
                :fill="showIpsilateralAir ? '#3f51b5' : '#d0d0d0'"
                :stroke="showIpsilateralAir ? '#3f51b5' : '#d0d0d0'"
                stroke-width="2"
              ></rect>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.34em"
              >
                I
              </text>
            </svg>
            <span v-if="haveContraIpsiEarData" :disabled="!showIpsilateralAir"
              >Ipsilateral ear</span
            >
            <v-checkbox
              v-if="haveContraIpsiEarData"
              v-model="showIpsilateralAir"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>
            <!-- contralateral/ipsilateral -->

            <v-btn
              v-if="showAddButton"
              small
              color="primary"
              class="ml-3"
              @click="addUserAudiogramDialog = true"
              ><v-icon small color="white darken-3">mdi-plus</v-icon>
              {{ addButtonLabel }}</v-btn
            >
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-row v-if="showControls">
      <v-col cols="1">
        <v-container fluid class="ma-0 pa-0" style="position: relative;">
          <v-row align="center" justify="end" class="pt-1">
            <span class="pr-3">Bone:</span>
          </v-row>
        </v-container>
      </v-col>
      <v-col cols="11">
        <v-container
          fluid
          fill-width
          class="ma-0 pa-0"
          :style="'position:relative;'"
        >
          <v-row align="center" justify="start">
            <svg width="20" height="20" class="ma-2">
              <line
                x1="9"
                y1="0"
                x2="18"
                y2="9"
                fill="none"
                :stroke="showLeftBone ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
              <line
                x1="18"
                y1="9"
                x2="9"
                y2="18"
                fill="none"
                :stroke="showLeftBone ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
            </svg>
            <span :disabled="!showLeftBone">Left ear</span>
            <v-checkbox
              v-model="showLeftBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <svg width="20" height="20" class="ma-2 ml-5">
              <line
                x1="18"
                y1="0"
                x2="9"
                y2="9"
                fill="none"
                :stroke="showRightBone ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
              <line
                x1="9"
                y1="9"
                x2="18"
                y2="18"
                fill="none"
                :stroke="showRightBone ? 'black' : '#d0d0d0'"
                stroke-width="2"
              ></line>
            </svg>
            <span :disabled="!showRightBone">Right ear</span>
            <v-checkbox
              v-model="showRightBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <!-- affected/unaffected -->
            <svg
              v-if="haveAffectedEarData"
              width="20"
              height="20"
              class="ma-2"
              style="overflow: visible;"
            >
              <g transform="translate(10,-5) rotate(45)">
                <rect
                  width="18"
                  height="18"
                  x="1"
                  y="1"
                  :fill="showAffectedBone ? '#3f51b5' : '#d0d0d0'"
                  :stroke="showAffectedBone ? '#3f51b5' : '#d0d0d0'"
                  stroke-width="2"
                ></rect>
              </g>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.30em"
              >
                A
              </text>
            </svg>
            <span v-if="haveAffectedEarData" :disabled="!showAffectedBone"
              >Affected ear</span
            >
            <v-checkbox
              v-if="haveAffectedEarData"
              v-model="showAffectedBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <svg
              v-if="haveAffectedEarData"
              width="20"
              height="20"
              class="ma-2 ml-5"
              style="overflow: visible;"
            >
              <g transform="translate(10,-5) rotate(45)">
                <rect
                  width="18"
                  height="18"
                  x="1"
                  y="1"
                  :fill="showUnaffectedBone ? '#3f51b5' : '#d0d0d0'"
                  :stroke="showUnaffectedBone ? '#3f51b5' : '#d0d0d0'"
                  stroke-width="2"
                ></rect>
              </g>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.30em"
              >
                U
              </text>
            </svg>
            <span v-if="haveAffectedEarData" :disabled="!showUnaffectedBone"
              >Unaffected ear</span
            >
            <v-checkbox
              v-if="haveAffectedEarData"
              v-model="showUnaffectedBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>
            <!-- affected/unaffected -->

            <!-- contralateral/ipsilateral -->
            <svg
              v-if="haveContraIpsiEarData"
              width="20"
              height="20"
              class="ma-2"
              style="overflow: visible;"
            >
              <g transform="translate(10,-5) rotate(45)">
                <rect
                  width="18"
                  height="18"
                  x="1"
                  y="1"
                  :fill="showContralateralBone ? '#3f51b5' : '#d0d0d0'"
                  :stroke="showContralateralBone ? '#3f51b5' : '#d0d0d0'"
                  stroke-width="2"
                ></rect>
              </g>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.30em"
              >
                C
              </text>
            </svg>
            <span
              v-if="haveContraIpsiEarData"
              :disabled="!showContralateralBone"
              >Contralateral ear</span
            >
            <v-checkbox
              v-if="haveContraIpsiEarData"
              v-model="showContralateralBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>

            <svg
              v-if="haveContraIpsiEarData"
              width="20"
              height="20"
              class="ma-2 ml-5"
              style="overflow: visible;"
            >
              <g transform="translate(10,-5) rotate(45)">
                <rect
                  width="18"
                  height="18"
                  x="1"
                  y="1"
                  :fill="showIpsilateralBone ? '#3f51b5' : '#d0d0d0'"
                  :stroke="showIpsilateralBone ? '#3f51b5' : '#d0d0d0'"
                  stroke-width="2"
                ></rect>
              </g>
              <text
                x="10"
                y="10"
                text-anchor="middle"
                fill="white"
                stroke="white"
                dy="0.30em"
              >
                I
              </text>
            </svg>
            <span v-if="haveContraIpsiEarData" :disabled="!showIpsilateralBone"
              >Ipsilateral ear</span
            >
            <v-checkbox
              v-if="haveContraIpsiEarData"
              v-model="showIpsilateralBone"
              hide-details
              class="px-2 pt-0 mt-0"
            ></v-checkbox>
            <!-- contralateral/ipsilateral -->

            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  color="primary"
                  v-bind="attrs"
                  class="ml-3"
                  v-on="on"
                  >Options</v-btn
                >
              </template>
              <v-card>
                <v-list>
                  <v-subheader>Display:</v-subheader>
                  <v-divider> </v-divider>
                  <v-list-item v-if="haveHighFrequencyData" class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showHighFreqs"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >Ultra-high frequencies</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showErrorBars"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >Error bars ({{
                          showMedian ? 'Q1-Q3' : '+/- 1 SD'
                        }})</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="jitterGroups"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Jitter study groups</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showBoxplots"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Boxplots</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showMedian"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Median</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showConnectingLines"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Connecting lines</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showGrid"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Grid</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showHearingLossZones"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Hearing loss zones</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0">
                    <v-list-item-action
                      ><v-checkbox
                        v-model="showSignificantFreqs"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                      ></v-checkbox
                    ></v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >Frequencies with p &lt;=
                        {{ pval_threshold }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  color="primary"
                  v-bind="attrs"
                  class="ml-3"
                  v-on="on"
                  >Size</v-btn
                >
              </template>
              <v-card>
                <v-list v-model="plotSize">
                  <v-subheader>Size:</v-subheader>
                  <v-divider> </v-divider>
                  <v-list-item class="py-0 my-0" value="small">
                    <v-list-item-action>
                      <v-checkbox
                        v-model="plotSize"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                        value="small"
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>small</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0" value="medium">
                    <v-list-item-action>
                      <v-checkbox
                        v-model="plotSize"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                        value="medium"
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>medium</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="py-0 my-0" value="large">
                    <v-list-item-action>
                      <v-checkbox
                        v-model="plotSize"
                        hide-details
                        dense
                        class="px-2 pt-0 mt-0"
                        value="large"
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>large</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-row>
      <add-user-audiogram-dialog
        :show="addUserAudiogramDialog"
        @dialogClosed="addUserAudiogramDialog = false"
        @addAudiogram="addAudiogram"
        @removeAllUserAudiograms="removeAllUserAudiograms"
      >
      </add-user-audiogram-dialog>

      <export-plot-dialog
        :show="exportPlotDialog"
        :default-filename-prefix="getDownloadFilename()"
        :export-options="
          haveHighFrequencyData
            ? ['standard frequencies', 'high frequencies']
            : []
        "
        @dialogClosed="exportPlotDialog = false"
        @exportPlot="exportPlot"
      >
      </export-plot-dialog>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';
import AudiogramPlot from '@/components/DataExplorer/AudiogramPlot.vue';
import AddUserAudiogramDialog from '@/components/DataExplorer/AddUserAudiogramDialog.vue';
import ExportPlotDialog from '@/components/common/ExportPlotDialog.vue';
import {
  getCollectionPureToneAudiometryFrequencyRange,
  getCollectionHasAffectedEarAudiometryData,
  getCollectionHasContralateralIpsilateralEarAudiometryData,
} from '@/utils/helpers';

export default {
  components: {
    AudiogramPlot,
    AddUserAudiogramDialog,
    ExportPlotDialog,
  },
  props: {
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    visibleCohorts: {
      type: Array,
      required: true,
      default: () => [],
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    showHighFreqsDefault: {
      type: Boolean,
      required: false,
      default: true,
    },
    title: {
      type: String,
      required: false,
      default: 'Audiogram',
    },
    showTitleBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    exportButtonSize: {
      type: String,
      required: false,
      default: 'small',
    },
    showAddButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    addButtonLabel: {
      type: String,
      required: false,
      default: 'Add Patient',
    },
  },
  data() {
    return {
      audiograms: [],
      userAudiograms: [],
      addUserAudiogramDialog: false,
      showLeftAir: true,
      showRightAir: true,
      showLeftBone: false,
      showRightBone: false,
      showAffectedAir: false,
      showUnaffectedAir: false,
      showAffectedBone: false,
      showUnaffectedBone: false,
      showContralateralAir: false,
      showIpsilateralAir: false,
      showContralateralBone: false,
      showIpsilateralBone: false,
      showLinkingLines: true,
      showBoxplots: false,
      showErrorBars: false,
      jitterGroups: false,
      showHighFreqs: this.showHighFreqsDefault,
      showMedian: false,
      showConnectingLines: true,
      showGrid: true,
      showHearingLossZones: true,
      showSignificantFreqs: true,
      exportPlotDialog: false,
      plotSize: 'medium',
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      collection: state.COLLECTION,
      pval_threshold: state.PVAL_THRESHOLD,
    }),
    haveHighFrequencyData() {
      if (this.analysis.collection == null) return false;
      let range = getCollectionPureToneAudiometryFrequencyRange(
        this.analysis.collection
      );
      return range['max'] >= 8500;
    },
    haveAffectedEarData() {
      return getCollectionHasAffectedEarAudiometryData(
        this.analysis.collection
      );
    },
    haveContraIpsiEarData() {
      return getCollectionHasContralateralIpsilateralEarAudiometryData(
        this.analysis.collection
      );
    },
  },
  watch: {},
  mounted() {
    // enable affected/unaffected checkboxes
    if (this.haveAffectedEarData) {
      this.showAffectedAir = true;
      this.showUnaffectedAir = true;
      this.showAffectedBone = true;
      this.showUnaffectedBone = true;

      this.showLeftAir = false;
      this.showRightAir = false;
      this.showLeftBone = false;
      this.showLeftBone = false;
    }
    // enable contralateral/ipsilateral checkboxes
    else if (this.haveContraIpsiEarData) {
      this.showContralateralAir = true;
      this.showIpsilateralAir = true;
      this.showContralateralBone = true;
      this.showIpsilateralBone = true;

      this.showLeftAir = false;
      this.showRightAir = false;
      this.showLeftBone = false;
      this.showLeftBone = false;
    }
  },
  methods: {
    addAudiogram(ag) {
      // direct the audiogram to regular or high frequency plot
      let all_freqs = [];
      let ears = ['L', 'R', 'A', 'U', 'C', 'I'];
      ears.forEach(ear => {
        let fe = ear in ag ? Object.keys(ag[ear]['freqs']) : [];
        all_freqs = [...fe, ...all_freqs];
      });

      all_freqs = all_freqs.sort().reverse();

      if (all_freqs.length > 0) {
        if (all_freqs[0] < 8500) {
          this.$refs.standard_freqs.addAudiogram(ag);
        } else if (this.$refs.high_freqs) {
          this.$refs.high_freqs.addAudiogram(ag);
        }
      }
    },
    removeAllUserAudiograms() {
      this.$refs.standard_freqs.removeAllUserAudiograms();
      if ('high_freqs' in this.$refs && this.$refs.high_freqs != null)
        this.$refs.high_freqs.removeAllUserAudiograms();
    },
    exportPlot(d) {
      this.exportPlotDialog = false;
      if (d['format'] == 'png') {
        this.downloadPng(d['prefix'] + '.png', d['option']);
      } else if (d['format'] == 'svg') {
        this.downloadSvg(d['prefix'], d['option']);
      }
    },
    getDownloadFilename() {
      let filename =
        'podvis_d' +
        this.collection.id +
        '_a' +
        this.analysis.index +
        '_audiograms';
      return filename;
    },
    downloadSvg(filename, which) {
      if (which == null || which == 'standard frequencies') {
        this.$refs.standard_freqs.downloadSvg(filename);
      } else {
        this.$refs.high_freqs.downloadSvg(filename);
      }
    },
    downloadPng(filename, which) {
      if (which == null || which == 'standard frequencies') {
        this.$refs.standard_freqs.downloadPng(filename);
      } else {
        this.$refs.high_freqs.downloadPng(filename);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
div .container {
  padding: 0px important;
}
</style>
